import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Simple Customer Icon SVG
const CustomerIcon = ({ size = 48 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" fill="#4285F4" />
    <path d="M12 14C7.58172 14 4 16.6863 4 20V22H20V20C20 16.6863 16.4183 14 12 14Z" fill="#4285F4" />
  </svg>
);

// Simple Service Provider Icon SVG
const ServiceProviderIcon = ({ size = 48 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 6H16V4C16 2.89 15.11 2 14 2H10C8.89 2 8 2.89 8 4V6H4C2.89 6 2 6.89 2 8V19C2 20.11 2.89 21 4 21H20C21.11 21 22 20.11 22 19V8C22 6.89 21.11 6 20 6ZM10 4H14V6H10V4ZM20 19H4V8H20V19Z" fill="#4285F4" />
    <path d="M7 10H9V12H7V10ZM11 10H13V12H11V10ZM15 10H17V12H15V10ZM7 14H9V16H7V14ZM11 14H13V16H11V14ZM15 14H17V16H15V14Z" fill="#4285F4" />
  </svg>
);

const SignupPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<"customer" | "serviceProvider" | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleContinue = () => {
    if (selectedType === "customer") {
      navigate("/registro/customer");
    } else if (selectedType === "serviceProvider") {
      navigate("/registro/worker");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f5f5f3",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "120px 20px 20px 20px",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "24px",
          padding: isMobile ? "32px 24px" : "40px 60px",
          maxWidth: "700px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? "24px" : "28px",
        }}
      >
        {/* Section 1: Heading */}
        <div style={{ textAlign: "center" }}>
          <h1
            style={{
              fontSize: isMobile ? "22px" : "28px",
              fontWeight: 600,
              color: "#1e293b",
              margin: 0,
              lineHeight: "1.4",
            }}
          >
            {t("registerAsCustomerOrServiceProvider")}
          </h1>
        </div>

        {/* Section 2: Two selection boxes */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: "32px",
            justifyContent: "center",
            alignItems: "stretch",
          }}
        >
          {/* Customer Box */}
          <div
            onClick={() => setSelectedType("customer")}
            style={{
              border: selectedType === "customer" ? "2px solid #4285F4" : "2px solid #e5e7eb",
              borderRadius: "16px",
              padding: "16px 20px",
              cursor: "pointer",
              backgroundColor: selectedType === "customer" ? "#eff6ff" : "white",
              transition: "all 0.2s ease",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "12px",
              width: isMobile ? "100%" : "220px",
            }}
          >
            <CustomerIcon size={40} />
            <div
              style={{
                fontSize: "20px",
                fontWeight: 500,
                color: "#1e293b",
                textAlign: "left",
              }}
            >
              {t("imACustomer")}
            </div>
            <div
              style={{
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                border: selectedType === "customer" ? "2px solid #4285F4" : "2px solid #9ca3af",
                backgroundColor: selectedType === "customer" ? "#4285F4" : "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              {selectedType === "customer" && (
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                  }}
                />
              )}
            </div>
          </div>

          {/* Service Provider Box */}
          <div
            onClick={() => setSelectedType("serviceProvider")}
            style={{
              border: selectedType === "serviceProvider" ? "2px solid #4285F4" : "2px solid #e5e7eb",
              borderRadius: "16px",
              padding: "16px 20px",
              cursor: "pointer",
              backgroundColor: selectedType === "serviceProvider" ? "#eff6ff" : "white",
              transition: "all 0.2s ease",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "12px",
              width: isMobile ? "100%" : "220px",
            }}
          >
            <ServiceProviderIcon size={40} />
            <div
              style={{
                fontSize: "20px",
                fontWeight: 500,
                color: "#1e293b",
                textAlign: "left",
              }}
            >
              {t("imAServiceProvider")}
            </div>
            <div
              style={{
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                border: selectedType === "serviceProvider" ? "2px solid #4285F4" : "2px solid #9ca3af",
                backgroundColor: selectedType === "serviceProvider" ? "#4285F4" : "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              {selectedType === "serviceProvider" && (
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-center">
          <p className="text-xs text-gray-500">
            {t("selectRoleContinue")}
          </p>
        </div>
        {/* Section 3: Continue button and login link */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <button
              onClick={handleContinue}
              disabled={!selectedType}
              style={{
                width: isMobile ? "100%" : "calc(220px + 32px)",
                padding: "12px 32px",
                fontSize: "15px",
                fontWeight: 600,
                color: "white",
                backgroundColor: selectedType ? "#4285F4" : "#9ca3af",
                border: "none",
                borderRadius: "50px",
                cursor: selectedType ? "pointer" : "not-allowed",
                transition: "all 0.2s ease",
              }}
            onMouseEnter={(e) => {
              if (selectedType) {
                e.currentTarget.style.backgroundColor = "#2563eb";
              }
            }}
            onMouseLeave={(e) => {
              if (selectedType) {
                e.currentTarget.style.backgroundColor = "#4285F4";
              }
            }}
            >
              {t("continue")}
            </button>
          </div>

          <div
            style={{
              fontSize: "14px",
              color: "#64748b",
              textAlign: "center",
            }}
          >
            {t("doYouAlreadyHaveAnAccount")}{" "}
            <Link
              to="/ingresar"
              style={{
                color: "#4285F4",
                textDecoration: "none",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              {t("login")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
